/**
 * Sample joystick sessions for the signed-in Joystick Sessions screen.
 * Replace this module when the sessions API is ready.
 * Header, filters, list, map, stats, timeline, and device card each have a skeleton.
 */

export type SessionStatus = 'completed' | 'live';
export type PointKind = 'start' | 'via' | 'end';
export type SessionSort = 'latest' | 'oldest' | 'distance' | 'duration' | 'speed';
export type SessionChip = 'all' | 'recent' | 'favourites' | 'live';
export type SessionRange = 'all' | 'today' | 'week' | 'month';

export interface MapLabel {
  x: number;
  y: number;
  text: string;
  sub?: string;
  strong?: boolean;
}

export interface RoutePoint {
  id: string;
  kind: PointKind;
  title: string;
  timeLabel: string;
  clockLabel: string;
  latitude: number;
  longitude: number;
  accuracyM: number;
  speedKmh: number | null;
  place: string;
  /** 0–100 across the illustrated map. */
  x: number;
  /** 0–100 down the illustrated map. */
  y: number;
}

export interface SeriesPoint {
  t: number;
  speed: number;
}

export interface ElevationPoint {
  km: number;
  meters: number;
}

export interface JoystickSession {
  id: string;
  name: string;
  status: SessionStatus;
  favourite: boolean;
  dateLabel: string;
  timeLabel: string;
  rangeLabel: string;
  area: string;
  startedAt: number;
  /** Lower rank floats to the top of "Latest first". */
  boardRank: number;
  durationSec: number;
  distanceKm: number;
  avgSpeedKmh: number;
  maxSpeedKmh: number;
  movingSec: number;
  stationarySec: number;
  gpsAccuracyM: number;
  updateIntervalSec: number;
  deviceId: string;
  deviceName: string;
  deviceOnline: boolean;
  lastSeenLabel: string;
  points: RoutePoint[];
  speedSeries: SeriesPoint[];
  elevationSeries: ElevationPoint[];
  /** Precomputed bars. Null means derive them from the speed series. */
  buckets: number[] | null;
  labels: MapLabel[];
  minElevationM: number;
  maxElevationM: number;
}

export interface SessionDevice {
  id: string;
  name: string;
}

export const PAGE_SIZE = 6;

export const SPEED_BANDS = ['0-20', '20-40', '40-80', '80-120', '120-160', '160-200', '200+'] as const;

/** Design clock. Filters stay on this day so the mock catalog does not drift. */
export const JOYSTICK_NOW = Date.parse('2026-10-25T19:30:00+05:30');

export const SESSION_DEVICES: SessionDevice[] = [
  { id: 'samsung-f42', name: 'Samsung F42' },
  { id: 'pixel-8', name: 'Pixel 8' },
  { id: 'oneplus-12', name: 'OnePlus 12' },
  { id: 'iqoo-neo', name: 'iQOO Neo 9' },
];

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

function cleanSpace(value: string): string {
  return value.replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
}

export function formatClock(totalSec: number): string {
  const whole = Math.max(0, Math.floor(totalSec));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = whole % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function formatPlayer(totalSec: number): string {
  const whole = Math.max(0, Math.floor(totalSec));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = whole % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}

export function formatDistance(km: number): string {
  const rounded = km >= 10 ? km.toFixed(1) : km.toFixed(2);
  const trimmed = rounded.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return `${trimmed} km`;
}

export function formatSpeed(kmh: number): string {
  return `${kmh.toFixed(1)} km/h`;
}

export function formatPercent(part: number, total: number): string {
  if (total <= 0) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

export function dayKey(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(ms);
}

export function formatCardDate(ms: number): string {
  return cleanSpace(new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(ms));
}

export function formatCardTime(ms: number): string {
  return cleanSpace(new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(ms));
}

function formatPointClock(ms: number, withSeconds: boolean): string {
  return cleanSpace(new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
    second: withSeconds ? '2-digit' : undefined,
    hour12: true,
  }).format(ms));
}

export function formatRangeLabel(startMs: number, durationSec: number): string {
  const endMs = startMs + durationSec * 1000;
  const date = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(startMs).replace(/,/g, '');
  const phrase = durationSec < 60 ? `${durationSec} sec` : formatPlayer(durationSec);
  return `${date} · ${formatCardTime(startMs)} – ${formatCardTime(endMs)} (${phrase})`;
}

export function speedBuckets(series: SeriesPoint[]): number[] {
  const bins = [0, 0, 0, 0, 0, 0, 0];
  const edges = [20, 40, 80, 120, 160, 200, Infinity];
  series.forEach((sample) => {
    const index = edges.findIndex((edge) => sample.speed < edge);
    bins[index < 0 ? bins.length - 1 : index] += 1;
  });
  return bins;
}

export function pageCount(total: number): number {
  if (total <= 0) return 1;
  return Math.ceil(total / PAGE_SIZE);
}

export function isRecent(startedAt: number, now = JOYSTICK_NOW): boolean {
  return startedAt >= now - WEEK_MS && startedAt <= now;
}

export function matchesRange(startedAt: number, range: SessionRange, now = JOYSTICK_NOW): boolean {
  if (range === 'today') return dayKey(startedAt) === dayKey(now);
  if (range === 'week') return isRecent(startedAt, now);
  if (range === 'month') return startedAt >= now - MONTH_MS && startedAt <= now;
  return true;
}

export function matchesQuery(session: JoystickSession, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [session.name, session.area, session.deviceName, session.status, session.dateLabel]
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

export function sortSessions(list: JoystickSession[], sort: SessionSort): JoystickSession[] {
  const next = [...list];
  next.sort((a, b) => {
    if (sort === 'oldest') return a.startedAt - b.startedAt || a.name.localeCompare(b.name);
    if (sort === 'distance') return b.distanceKm - a.distanceKm || a.name.localeCompare(b.name);
    if (sort === 'duration') return b.durationSec - a.durationSec || a.name.localeCompare(b.name);
    if (sort === 'speed') return b.maxSpeedKmh - a.maxSpeedKmh || a.name.localeCompare(b.name);
    return a.boardRank - b.boardRank || b.startedAt - a.startedAt;
  });
  return next;
}

export function sessionSummary(session: JoystickSession): string {
  return [
    session.name,
    session.rangeLabel,
    session.area,
    `${formatDistance(session.distanceKm)} · ${formatClock(session.durationSec)}`,
    `Avg ${formatSpeed(session.avgSpeedKmh)} · Max ${formatSpeed(session.maxSpeedKmh)}`,
  ].join('\n');
}

export function sessionToJson(session: JoystickSession): string {
  return JSON.stringify({
    name: session.name,
    status: session.status,
    area: session.area,
    startedAt: new Date(session.startedAt).toISOString(),
    distanceKm: session.distanceKm,
    durationSec: session.durationSec,
    avgSpeedKmh: session.avgSpeedKmh,
    maxSpeedKmh: session.maxSpeedKmh,
    device: session.deviceName,
    points: session.points.map((point) => ({
      title: point.title,
      time: point.clockLabel,
      latitude: point.latitude,
      longitude: point.longitude,
      accuracyM: point.accuracyM,
      speedKmh: point.speedKmh,
      place: point.place,
    })),
  }, null, 2);
}

function deviceById(id: string): SessionDevice {
  return SESSION_DEVICES.find((device) => device.id === id) ?? SESSION_DEVICES[0];
}

function rampSpeed(durationSec: number, avg: number, max: number): SeriesPoint[] {
  const steps = 8;
  return Array.from({ length: steps }, (_, index) => {
    const wave = Math.sin((index / (steps - 1)) * Math.PI);
    return {
      t: Math.round((durationSec * index) / (steps - 1)),
      speed: Number((avg * 0.35 + (max - avg * 0.35) * wave).toFixed(1)),
    };
  });
}

function rampElevation(distanceKm: number, min: number, max: number): ElevationPoint[] {
  const steps = 7;
  return Array.from({ length: steps }, (_, index) => ({
    km: Number(((distanceKm * index) / (steps - 1)).toFixed(2)),
    meters: Math.round(min + ((max - min) * index) / (steps - 1)),
  }));
}

interface Spot {
  kind: PointKind;
  x: number;
  y: number;
  place: string;
  speedKmh: number | null;
  accuracyM?: number;
  latitude?: number;
  longitude?: number;
  timeLabel?: string;
  clockLabel?: string;
}

function buildPoints(id: string, startMs: number, durationSec: number, spots: Spot[]): RoutePoint[] {
  return spots.map((spot, index) => {
    const offset = spots.length === 1 ? 0 : Math.round((durationSec * index) / (spots.length - 1));
    const at = startMs + offset * 1000;
    const latitude = spot.latitude ?? Number((28.7 - spot.y * 0.00115).toFixed(4));
    const longitude = spot.longitude ?? Number((77.2 + spot.x * 0.00145).toFixed(4));
    const viaNumber = spots.slice(0, index + 1).filter((item) => item.kind === 'via').length;
    let title = 'Start Point';
    if (spot.kind === 'end') title = 'End Point';
    if (spot.kind === 'via') title = `Via Point ${viaNumber}`;
    return {
      id: `${id}-p${index + 1}`,
      kind: spot.kind,
      title,
      timeLabel: spot.timeLabel ?? formatPointClock(at, false),
      clockLabel: spot.clockLabel ?? formatPointClock(at, true),
      latitude,
      longitude,
      accuracyM: spot.accuracyM ?? 5,
      speedKmh: spot.speedKmh,
      place: spot.place,
      x: spot.x,
      y: spot.y,
    };
  });
}

function areaLabels(area: string): MapLabel[] {
  const short = area.split(',')[0];
  return [
    { x: 30, y: 28, text: short, strong: true },
    { x: 68, y: 58, text: 'Route' },
  ];
}

const SUNDAY_START = Date.parse('2026-10-25T08:19:00+05:30');

const HEROES: JoystickSession[] = [
  {
    id: 'sunday-morning',
    name: 'Sunday morning route',
    status: 'completed',
    favourite: true,
    dateLabel: 'Oct 25, 2026',
    timeLabel: '8:19 AM',
    rangeLabel: 'Sun 25 Oct 2026 · 8:19 AM – 8:20 AM (25 sec)',
    area: 'Laxmi Nagar, Delhi',
    startedAt: SUNDAY_START,
    boardRank: 0,
    durationSec: 25,
    distanceKm: 1.03,
    avgSpeedKmh: 145.5,
    maxSpeedKmh: 194,
    movingSec: 25,
    stationarySec: 0,
    gpsAccuracyM: 5,
    updateIntervalSec: 0.5,
    deviceId: 'samsung-f42',
    deviceName: 'Samsung F42',
    deviceOnline: true,
    lastSeenLabel: '2 min ago',
    buckets: [2, 3, 5, 8, 12, 20, 6],
    minElevationM: 210,
    maxElevationM: 228,
    labels: [
      { x: 44, y: 16, text: 'Hira Sweets', sub: 'हीरा स्वीट्स' },
      { x: 24, y: 38, text: 'Laxmi Nagar', sub: 'लक्ष्मी नगर', strong: true },
      { x: 32, y: 52, text: 'ALCOMART', sub: 'अल्कोमार्ट' },
      { x: 18, y: 84, text: 'Shakarpur', sub: 'शकरपुर' },
      { x: 58, y: 18, text: 'Mandawali-Chandrawal Vihar' },
      { x: 56, y: 68, text: 'Mandir Rd' },
    ],
    speedSeries: [
      { t: 0, speed: 12 },
      { t: 4, speed: 42 },
      { t: 8, speed: 96 },
      { t: 12, speed: 168 },
      { t: 16, speed: 194 },
      { t: 21, speed: 188 },
      { t: 25, speed: 154 },
    ],
    elevationSeries: [
      { km: 0, meters: 210 },
      { km: 0.15, meters: 214 },
      { km: 0.35, meters: 218 },
      { km: 0.55, meters: 221 },
      { km: 0.75, meters: 224 },
      { km: 0.9, meters: 226 },
      { km: 1.03, meters: 228 },
    ],
    points: buildPoints('sunday-morning', SUNDAY_START, 25, [
      {
        kind: 'start', x: 20, y: 76, place: 'Laxmi Nagar, Delhi', speedKmh: null,
        latitude: 28.6285, longitude: 77.2797, timeLabel: '8:19 AM', clockLabel: '8:19:00 AM',
      },
      {
        kind: 'via', x: 48, y: 60, place: 'Laxmi Nagar, Delhi', speedKmh: 194,
        latitude: 28.6285, longitude: 77.2814, timeLabel: '8:19 AM', clockLabel: '8:19:08 AM',
      },
      {
        kind: 'via', x: 60, y: 44, place: 'Laxmi Nagar, Delhi', speedKmh: 194,
        latitude: 28.6303, longitude: 77.2806, timeLabel: '8:19 AM', clockLabel: '8:19:15 AM',
      },
      {
        kind: 'end', x: 78, y: 28, place: 'Mandawali, Delhi', speedKmh: null,
        latitude: 28.6333, longitude: 77.2847, timeLabel: '8:20 AM', clockLabel: '8:20:00 AM',
      },
    ]),
  },
  hero({
    id: 'market-loop',
    name: 'Market loop test',
    iso: '2026-10-24T18:14:00+05:30',
    dateLabel: 'Oct 24, 2026',
    timeLabel: '6:14 PM',
    area: 'Shakarpur, Delhi',
    boardRank: 1,
    durationSec: 252,
    distanceKm: 2.8,
    avgSpeedKmh: 40,
    maxSpeedKmh: 72,
    deviceId: 'pixel-8',
    lastSeenLabel: '18 min ago',
    minElevationM: 208,
    maxElevationM: 219,
    spots: [
      { kind: 'start', x: 30, y: 72, place: 'Shakarpur, Delhi', speedKmh: null },
      { kind: 'via', x: 58, y: 42, place: 'Shakarpur, Delhi', speedKmh: 72 },
      { kind: 'via', x: 40, y: 30, place: 'Shakarpur, Delhi', speedKmh: 54 },
      { kind: 'end', x: 26, y: 56, place: 'Shakarpur, Delhi', speedKmh: null },
    ],
  }),
  hero({
    id: 'commute-sim',
    name: 'Commute simulation',
    iso: '2026-10-25T09:32:00+05:30',
    dateLabel: 'Oct 25, 2026',
    timeLabel: '9:32 AM',
    area: 'Preet Vihar, Delhi',
    boardRank: 2,
    durationSec: 516,
    distanceKm: 4.6,
    avgSpeedKmh: 32.1,
    maxSpeedKmh: 68,
    deviceId: 'samsung-f42',
    lastSeenLabel: '5 min ago',
    minElevationM: 206,
    maxElevationM: 224,
    spots: [
      { kind: 'start', x: 16, y: 80, place: 'Preet Vihar, Delhi', speedKmh: null },
      { kind: 'via', x: 34, y: 62, place: 'Preet Vihar, Delhi', speedKmh: 48 },
      { kind: 'via', x: 52, y: 48, place: 'Preet Vihar, Delhi', speedKmh: 68 },
      { kind: 'via', x: 70, y: 34, place: 'Preet Vihar, Delhi', speedKmh: 44 },
      { kind: 'end', x: 86, y: 20, place: 'Preet Vihar, Delhi', speedKmh: null },
    ],
  }),
  hero({
    id: 'evening-walk',
    name: 'Evening walk',
    iso: '2026-10-25T19:11:00+05:30',
    dateLabel: 'Oct 25, 2026',
    timeLabel: '7:11 PM',
    area: 'Anand Vihar, Delhi',
    boardRank: 3,
    durationSec: 381,
    distanceKm: 3.2,
    avgSpeedKmh: 30.2,
    maxSpeedKmh: 44,
    status: 'live',
    deviceId: 'samsung-f42',
    lastSeenLabel: 'Just now',
    minElevationM: 212,
    maxElevationM: 220,
    spots: [
      { kind: 'start', x: 24, y: 70, place: 'Anand Vihar, Delhi', speedKmh: null },
      { kind: 'via', x: 46, y: 56, place: 'Anand Vihar, Delhi', speedKmh: 36 },
      { kind: 'via', x: 62, y: 42, place: 'Anand Vihar, Delhi', speedKmh: 44 },
      { kind: 'end', x: 74, y: 34, place: 'Anand Vihar, Delhi', speedKmh: null },
    ],
  }),
  hero({
    id: 'test-route-1',
    name: 'Test route 1',
    iso: '2026-10-21T11:03:00+05:30',
    dateLabel: 'Oct 21, 2026',
    timeLabel: '11:03 AM',
    area: 'Mayur Vihar, Delhi',
    boardRank: 4,
    durationSec: 615,
    distanceKm: 5.1,
    avgSpeedKmh: 29.9,
    maxSpeedKmh: 61,
    deviceId: 'oneplus-12',
    deviceOnline: false,
    lastSeenLabel: 'Yesterday',
    minElevationM: 204,
    maxElevationM: 230,
    spots: [
      { kind: 'start', x: 14, y: 64, place: 'Mayur Vihar, Delhi', speedKmh: null },
      { kind: 'via', x: 36, y: 52, place: 'Mayur Vihar, Delhi', speedKmh: 40 },
      { kind: 'via', x: 56, y: 40, place: 'Mayur Vihar, Delhi', speedKmh: 61 },
      { kind: 'via', x: 72, y: 28, place: 'Mayur Vihar, Delhi', speedKmh: 33 },
      { kind: 'end', x: 88, y: 22, place: 'Mayur Vihar, Delhi', speedKmh: null },
    ],
  }),
  hero({
    id: 'office-home',
    name: 'Office to Home',
    iso: '2026-10-19T17:27:00+05:30',
    dateLabel: 'Oct 19, 2026',
    timeLabel: '5:27 PM',
    area: 'Noida, UP',
    boardRank: 5,
    durationSec: 1338,
    distanceKm: 12.4,
    avgSpeedKmh: 33.4,
    maxSpeedKmh: 86,
    movingSec: 1218,
    stationarySec: 120,
    deviceId: 'iqoo-neo',
    lastSeenLabel: '3 hr ago',
    gpsAccuracyM: 8,
    updateIntervalSec: 1,
    minElevationM: 198,
    maxElevationM: 236,
    spots: [
      { kind: 'start', x: 12, y: 84, place: 'Noida, UP', speedKmh: null },
      { kind: 'via', x: 28, y: 70, place: 'Noida, UP', speedKmh: 52 },
      { kind: 'via', x: 44, y: 56, place: 'Noida, UP', speedKmh: 86 },
      { kind: 'via', x: 62, y: 44, place: 'Noida, UP', speedKmh: 48 },
      { kind: 'via', x: 76, y: 30, place: 'Noida, UP', speedKmh: 40 },
      { kind: 'end', x: 90, y: 16, place: 'Noida, UP', speedKmh: null },
    ],
  }),
];

interface HeroInput {
  id: string;
  name: string;
  iso: string;
  dateLabel: string;
  timeLabel: string;
  area: string;
  boardRank: number;
  durationSec: number;
  distanceKm: number;
  avgSpeedKmh: number;
  maxSpeedKmh: number;
  deviceId: string;
  lastSeenLabel: string;
  minElevationM: number;
  maxElevationM: number;
  spots: Spot[];
  status?: SessionStatus;
  favourite?: boolean;
  movingSec?: number;
  stationarySec?: number;
  deviceOnline?: boolean;
  gpsAccuracyM?: number;
  updateIntervalSec?: number;
}

function hero(input: HeroInput): JoystickSession {
  const startedAt = Date.parse(input.iso);
  const movingSec = input.movingSec ?? input.durationSec;
  const stationarySec = input.stationarySec ?? 0;
  const device = deviceById(input.deviceId);
  return {
    id: input.id,
    name: input.name,
    status: input.status ?? 'completed',
    favourite: input.favourite ?? false,
    dateLabel: input.dateLabel,
    timeLabel: input.timeLabel,
    rangeLabel: formatRangeLabel(startedAt, input.durationSec),
    area: input.area,
    startedAt,
    boardRank: input.boardRank,
    durationSec: input.durationSec,
    distanceKm: input.distanceKm,
    avgSpeedKmh: input.avgSpeedKmh,
    maxSpeedKmh: input.maxSpeedKmh,
    movingSec,
    stationarySec,
    gpsAccuracyM: input.gpsAccuracyM ?? 5,
    updateIntervalSec: input.updateIntervalSec ?? 0.5,
    deviceId: device.id,
    deviceName: device.name,
    deviceOnline: input.deviceOnline ?? true,
    lastSeenLabel: input.lastSeenLabel,
    points: buildPoints(input.id, startedAt, input.durationSec, input.spots),
    speedSeries: rampSpeed(input.durationSec, input.avgSpeedKmh, input.maxSpeedKmh),
    elevationSeries: rampElevation(input.distanceKm, input.minElevationM, input.maxElevationM),
    buckets: null,
    labels: areaLabels(input.area),
    minElevationM: input.minElevationM,
    maxElevationM: input.maxElevationM,
  };
}

const GENERATED_NAMES = [
  'Park circuit', 'River edge', 'Station hop', 'Night drift', 'Campus loop',
  'Market run', 'Ring road', 'Lane check', 'Sunrise roll', 'Depot link',
  'Mall return', 'Metro link', 'Garden pass', 'Bridge test', 'Block loop',
  'Service lane', 'Canal path', 'School run', 'Clinic hop', 'Stadium lap',
];

const GENERATED_AREAS = [
  'Laxmi Nagar, Delhi',
  'Shakarpur, Delhi',
  'Preet Vihar, Delhi',
  'Anand Vihar, Delhi',
  'Mayur Vihar, Delhi',
  'Noida, UP',
  'Mandawali, Delhi',
  'Patparganj, Delhi',
  'Geeta Colony, Delhi',
  'Krishna Nagar, Delhi',
  'Karkardooma, Delhi',
  'Vaishali, Ghaziabad',
];

function mulberry32(seed: number): () => number {
  let state = seed;
  return () => {
    state += 0x6d2b79f5;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function buildGenerated(index: number): JoystickSession {
  const random = mulberry32(1000 + index * 17);
  const device = SESSION_DEVICES[index % SESSION_DEVICES.length];
  const area = GENERATED_AREAS[index % GENERATED_AREAS.length];
  const name = `${GENERATED_NAMES[index % GENERATED_NAMES.length]} ${Math.floor(index / GENERATED_NAMES.length) + 2}`;
  const startedAt = Date.parse('2026-10-18T18:00:00+05:30') - index * 9 * 60 * 60 * 1000;
  const distanceKm = Number((0.45 + random() * 14).toFixed(2));
  const avgSpeedKmh = Number((18 + random() * 80).toFixed(1));
  const maxSpeedKmh = Number((avgSpeedKmh + 8 + random() * 28).toFixed(1));
  const durationSec = Math.max(30, Math.round((distanceKm / Math.max(avgSpeedKmh, 1)) * 3600));
  const stationarySec = index % 5 === 0 ? Math.round(durationSec * 0.12) : 0;
  const movingSec = durationSec - stationarySec;
  const count = 3 + (index % 3);
  const spots: Spot[] = [];
  let x = 12 + (index % 6) * 2;
  let y = 80 - (index % 3) * 4;
  for (let point = 0; point < count; point += 1) {
    const kind: PointKind = point === 0 ? 'start' : point === count - 1 ? 'end' : 'via';
    spots.push({
      kind,
      x: Math.round(Math.min(92, Math.max(8, x))),
      y: Math.round(Math.min(90, Math.max(10, y))),
      place: kind === 'via' ? 'En route' : area,
      speedKmh: kind === 'via' ? maxSpeedKmh : null,
    });
    x += 8 + random() * 16;
    y -= 6 + random() * 16;
  }
  const minElevationM = Math.round(190 + random() * 20);
  const maxElevationM = minElevationM + Math.round(8 + random() * 24);
  const online = index % 4 !== 3;
  return {
    id: `generated-${index + 1}`,
    name,
    status: 'completed',
    favourite: index % 17 === 0,
    dateLabel: formatCardDate(startedAt),
    timeLabel: formatCardTime(startedAt),
    rangeLabel: formatRangeLabel(startedAt, durationSec),
    area,
    startedAt,
    boardRank: 6 + index,
    durationSec,
    distanceKm,
    avgSpeedKmh,
    maxSpeedKmh,
    movingSec,
    stationarySec,
    gpsAccuracyM: 4 + (index % 8),
    updateIntervalSec: index % 2 === 0 ? 0.5 : 1,
    deviceId: device.id,
    deviceName: device.name,
    deviceOnline: online,
    lastSeenLabel: online ? `${(index % 40) + 1} min ago` : 'Yesterday',
    points: buildPoints(`generated-${index + 1}`, startedAt, durationSec, spots),
    speedSeries: rampSpeed(durationSec, avgSpeedKmh, maxSpeedKmh),
    elevationSeries: rampElevation(distanceKm, minElevationM, maxElevationM),
    buckets: null,
    labels: areaLabels(area),
    minElevationM,
    maxElevationM,
  };
}

export const JOYSTICK_SESSIONS: JoystickSession[] = [
  ...HEROES,
  ...Array.from({ length: 122 }, (_, index) => buildGenerated(index)),
];

export function createJoystickSession(input: {
  name: string;
  area: string;
  deviceId: string;
}): JoystickSession {
  const now = Date.now();
  const device = deviceById(input.deviceId);
  const area = input.area.trim() || 'Custom area';
  const durationSec = 48;
  const distanceKm = 1.42;
  return {
    id: `custom-${now}`,
    name: input.name.trim(),
    status: 'completed',
    favourite: false,
    dateLabel: formatCardDate(now),
    timeLabel: formatCardTime(now),
    rangeLabel: formatRangeLabel(now, durationSec),
    area,
    startedAt: now,
    boardRank: -now,
    durationSec,
    distanceKm,
    avgSpeedKmh: 106.5,
    maxSpeedKmh: 132,
    movingSec: 48,
    stationarySec: 0,
    gpsAccuracyM: 5,
    updateIntervalSec: 0.5,
    deviceId: device.id,
    deviceName: device.name,
    deviceOnline: true,
    lastSeenLabel: 'Just now',
    buckets: null,
    minElevationM: 214,
    maxElevationM: 226,
    labels: areaLabels(area),
    speedSeries: rampSpeed(durationSec, 106.5, 132),
    elevationSeries: rampElevation(distanceKm, 214, 226),
    points: buildPoints(`custom-${now}`, now, durationSec, [
      { kind: 'start', x: 22, y: 74, place: area, speedKmh: null },
      { kind: 'via', x: 46, y: 54, place: area, speedKmh: 132 },
      { kind: 'via', x: 64, y: 38, place: area, speedKmh: 118 },
      { kind: 'end', x: 82, y: 24, place: area, speedKmh: null },
    ]),
  };
}
